import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { useNavigate, useParams } from "react-router-dom";
import AdminNavbar, { AdminTopBar } from "../components/AdminNavbar";
import { useAdminAccess } from "../context/AdminAccessContext";
import {
  createBlogPost,
  generateBlogSlug,
  getBlogPostById,
  updateBlogPost,
  uploadBlogCoverImage,
} from "../services/blogService";

const defaultForm = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  coverImageUrl: "",
  category: "",
  tags: "",
  author: "Darssy Team",
  status: "draft",
  seoTitle: "",
  seoDescription: "",
};

const requiredPublishFields = [
  "title",
  "slug",
  "excerpt",
  "content",
  "category",
  "coverImageUrl",
  "seoTitle",
  "seoDescription",
];

function toTags(value) {
  return value
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
}

export default function AdminBlogEditor() {
  const { blogId } = useParams();
  const { isMarketing } = useAdminAccess();
  const isEditing = Boolean(blogId);
  const navigate = useNavigate();
  const [form, setForm] = useState(defaultForm);
  const [slugEdited, setSlugEdited] = useState(false);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const missingPublishFields = useMemo(
    () => requiredPublishFields.filter((field) => !String(form[field] || "").trim()),
    [form]
  );

  useEffect(() => {
    if (!isEditing) return;

    const fetchPost = async () => {
      try {
        setLoading(true);
        const post = await getBlogPostById(blogId);
        if (!post) {
          setError("Blog post not found.");
          return;
        }
        setForm({
          title: post.title || "",
          slug: post.slug || "",
          excerpt: post.excerpt || "",
          content: post.content || "",
          coverImageUrl: post.coverImageUrl || "",
          category: post.category || "",
          tags: Array.isArray(post.tags) ? post.tags.join(", ") : "",
          author: post.author || "Darssy Team",
          status: post.status || "draft",
          seoTitle: post.seoTitle || "",
          seoDescription: post.seoDescription || "",
        });
        setSlugEdited(Boolean(post.slug));
      } catch (err) {
        console.error("Failed to load blog post:", err);
        setError("Could not load the blog post.");
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [blogId, isEditing]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === "title" && !slugEdited) {
        next.slug = generateBlogSlug(value);
      }
      if (field === "slug") {
        next.slug = generateBlogSlug(value);
      }
      return next;
    });
    if (field === "slug") setSlugEdited(true);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setError("");
      const url = await uploadBlogCoverImage(file);
      setForm((current) => ({ ...current, coverImageUrl: url }));
      setSuccess("Cover image uploaded.");
    } catch (err) {
      console.error("Failed to upload cover image:", err);
      setError("Could not upload the cover image.");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const buildPayload = (status) => ({
    title: form.title.trim(),
    slug: form.slug.trim(),
    excerpt: form.excerpt.trim(),
    content: form.content.trim(),
    coverImageUrl: form.coverImageUrl.trim(),
    category: form.category.trim(),
    tags: toTags(form.tags),
    author: form.author.trim() || "Darssy Team",
    status,
    seoTitle: form.seoTitle.trim(),
    seoDescription: form.seoDescription.trim(),
  });

  const handleSave = async (status) => {
    if (status === "published" && missingPublishFields.length > 0) {
      setError(`Before publishing, complete: ${missingPublishFields.join(", ")}.`);
      return;
    }

    try {
      setSaving(true);
      setError("");
      const payload = buildPayload(status);
      if (isEditing) {
        await updateBlogPost(blogId, payload);
      } else {
        await createBlogPost(payload);
      }
      setSuccess(status === "published" ? "Post published." : "Draft saved.");
      setTimeout(() => navigate("/06071966/blogs"), 500);
    } catch (err) {
      console.error("Failed to save blog post:", err);
      setError("Could not save the blog post.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AdminTopBar marketingMode={isMarketing} />
      <Box
        sx={{
          display: "flex",
          height: "calc(100vh - 64px)",
          mt: "64px",
          backgroundColor: "#ffffffff",
          flexDirection: "row-reverse",
          overflow: "hidden",
        }}
      >
        {!isMarketing && <AdminNavbar />}
        <Box component="main" sx={{ flexGrow: 1, overflowY: "auto", p: { xs: 1, md: 2 }, direction: "ltr" }}>
          <Card
            elevation={0}
            sx={{
              mb: 2.5,
              borderRadius: 4,
              px: 3,
              py: 2.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: "#eef2ff",
                  color: "#4f46e5",
                  width: 46,
                  height: 46,
                  boxShadow: "0 8px 18px rgba(79,70,229,0.18)",
                }}
              >
                <EditNoteOutlinedIcon />
              </Avatar>
              <Box sx={{ textAlign: "left" }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {isEditing ? "Edit Blog Post" : "Create Blog Post"}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Write useful articles that bring tutors and schools into Darssy.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1, direction: "ltr" }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/06071966/blogs")}
                disabled={saving}
                sx={{ borderRadius: 2 }}
              >
                Back
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SaveOutlinedIcon />}
                onClick={() => handleSave("draft")}
                disabled={saving || uploading}
                sx={{ borderRadius: 2 }}
              >
                Save as Draft
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<PublishOutlinedIcon />}
                onClick={() => handleSave("published")}
                disabled={saving || uploading}
                sx={{ borderRadius: 2 }}
              >
                Publish
              </Button>
            </Box>

          </Card>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

          {loading ? (
            <Paper
              elevation={0}
              sx={{
                py: 8,
                textAlign: "center",
                borderRadius: 2,
                boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
                border: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <CircularProgress />
            </Paper>
          ) : (
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={8}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: "#ffffff",
                    boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
                    border: "1px solid rgba(0,0,0,0.06)",
                    p: { xs: 2, md: 3 },
                    direction: "ltr",
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 800, fontSize: 18, color: "#111827" }}>Article Content</Typography>
                      <Typography sx={{ color: "#6b7280", fontSize: 14 }}>Main title, slug, excerpt, and long-form body.</Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: "#eef2ff", color: "#4f46e5" }}>
                      <ArticleOutlinedIcon />
                    </Avatar>
                  </Stack>
                  <Stack gap={2}>
                    <TextField label="Title" value={form.title} onChange={handleChange("title")} fullWidth required />
                    <TextField label="Slug" value={form.slug} onChange={handleChange("slug")} fullWidth required />
                    <TextField
                      label="Excerpt"
                      value={form.excerpt}
                      onChange={handleChange("excerpt")}
                      fullWidth
                      multiline
                      minRows={3}
                      required
                    />
                    <TextField
                      label="Full content"
                      value={form.content}
                      onChange={handleChange("content")}
                      fullWidth
                      multiline
                      minRows={18}
                      required
                    />
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Stack gap={2.5}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: "#ffffff",
                      boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
                      border: "1px solid rgba(0,0,0,0.06)",
                      p: 2.5,
                      direction: "ltr",
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 800, color: "#111827" }}>Cover and Details</Typography>
                        <Typography sx={{ color: "#6b7280", fontSize: 13 }}>Public card and article identity.</Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: "#e0f2fe", color: "#0369a1" }}>
                        <ImageOutlinedIcon />
                      </Avatar>
                    </Stack>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<CloudUploadOutlinedIcon />}
                      disabled={uploading}
                      sx={{ py: 1.4, width: "100%", borderRadius: 2 }}
                    >
                      {uploading ? "Uploading..." : "Upload Cover Image"}
                      <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                    </Button>
                    {form.coverImageUrl && (
                      <Box
                        component="img"
                        src={form.coverImageUrl}
                        alt="Blog cover preview"
                        sx={{ width: "100%", aspectRatio: "16 / 9", objectFit: "cover", borderRadius: 2 }}
                      />
                    )}
                    <Stack gap={2} sx={{ mt: 2 }}>
                      <TextField label="Cover image URL" value={form.coverImageUrl} onChange={handleChange("coverImageUrl")} fullWidth required />
                      <TextField label="Category" value={form.category} onChange={handleChange("category")} fullWidth required />
                      <TextField label="Tags" value={form.tags} onChange={handleChange("tags")} fullWidth helperText="Separate tags with commas." />
                      <TextField label="Author" value={form.author} onChange={handleChange("author")} fullWidth />
                    </Stack>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: "#ffffff",
                      boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
                      border: "1px solid rgba(0,0,0,0.06)",
                      p: 2.5,
                      direction: "ltr",
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 800, color: "#111827" }}>SEO Settings</Typography>
                        <Typography sx={{ color: "#6b7280", fontSize: 13 }}>Used by Google and social previews.</Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: "#dcfce7", color: "#16a34a" }}>
                        <SearchOutlinedIcon />
                      </Avatar>
                    </Stack>
                    <Stack gap={2}>
                      <TextField label="SEO title" value={form.seoTitle} onChange={handleChange("seoTitle")} fullWidth required />
                    <TextField
                      label="SEO description"
                      value={form.seoDescription}
                      onChange={handleChange("seoDescription")}
                      fullWidth
                      multiline
                      minRows={3}
                      required
                    />
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Chip
                          size="small"
                          label={form.status === "published" ? "Published" : "Draft"}
                          sx={{
                            bgcolor: form.status === "published" ? "rgba(34,197,94,0.12)" : "rgba(251,191,36,0.18)",
                            color: form.status === "published" ? "rgba(22,163,74,0.95)" : "#b45309",
                            fontWeight: 700,
                            borderRadius: "5px",
                          }}
                        />
                        <Chip
                          size="small"
                          label={`${missingPublishFields.length} missing publish fields`}
                          sx={{ borderRadius: "5px", fontWeight: 700 }}
                        />
                      </Box>
                    </Stack>
                  </Paper>
                </Stack>
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>
    </>
  );
}
